/**
 * Dev-only sandbox for the Talking Portrait. Reachable at /lab/instructor.
 * Renders VideoInstructor at multiple sizes with toggle controls so we
 * (and the testing agent) can verify mouth/blink alignment without
 * needing a seeded learning path.
 *
 * Not linked from any nav. Safe to leave shipped.
 */
import { useState } from "react";
import VideoInstructor from "@/components/VideoInstructor";
import useSpeechNarration from "@/hooks/useSpeechNarration";

const SAMPLE_TEXT =
  "Welcome to Oak AI Campus. Today we explore how memory consolidation transforms short-term study into durable mastery. Notice how repetition reshapes recall.";

export default function LabInstructor() {
  const [size, setSize] = useState(220);
  const { speak, stop, speaking, currentWord, supported } = useSpeechNarration({});
  // Force-render speaking state — useful for verifying viseme/blink
  // overlays in headless browsers where SpeechSynthesis is silent.
  const [forceSpeak, setForceSpeak] = useState(false);
  const [forceWord, setForceWord] = useState("about");
  const liveSpeaking = speaking || forceSpeak;
  const liveWord = forceSpeak ? forceWord : currentWord;

  const onToggle = () => (speaking ? stop() : speak(SAMPLE_TEXT));

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white p-8 flex flex-col items-center gap-6">
      <h1 className="font-display text-2xl">Talking Portrait — alignment lab</h1>
      <p className="text-sm text-white/55 max-w-md text-center">
        Tap "Read aloud" — the photo gets a subtle breathing scale, a warm vocal glow, an
        occasional natural blink, and a "● Live" badge. (We dropped the SVG mouth-viseme overlay
        because at this face resolution it always read as a smudge — karaoke text + TTS audio
        carries the talking signal more cleanly.)
      </p>

      <div className="flex flex-wrap gap-6 items-end justify-center">
        <VideoInstructor speaking={liveSpeaking} word={liveWord} size={140} />
        <VideoInstructor speaking={liveSpeaking} word={liveWord} size={size} />
        <VideoInstructor speaking={liveSpeaking} word={liveWord} size={300} />
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={onToggle}
          disabled={!supported}
          className="px-4 py-2 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#9bffcc] text-sm disabled:opacity-40"
          data-testid="lab-toggle"
        >
          {speaking ? "Stop" : "Read aloud"}
        </button>
        <button
          onClick={() => setForceSpeak((v) => !v)}
          className="px-3 py-2 rounded-full bg-[#ffb067]/15 border border-[#ffb067]/40 text-[#ffd6a8] text-xs"
          data-testid="lab-force-speak"
        >
          {forceSpeak ? "Stop force" : "Force speaking"}
        </button>
        <select
          value={forceWord}
          onChange={(e) => setForceWord(e.target.value)}
          className="bg-white/5 border border-white/15 rounded px-2 py-1 text-xs text-white"
        >
          {["about", "memory", "feel", "you", "moment", "every"].map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
        <input
          type="range"
          min={120}
          max={360}
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          className="accent-[#00FF9D]"
        />
        <span className="text-xs text-white/55 font-mono-oak">size={size} · word="{liveWord || "—"}"</span>
      </div>

      <div className="text-[11px] text-white/40 mt-2">
        {supported ? "Web Speech API available" : "Web Speech API NOT available — toggle is inert"}
      </div>
    </div>
  );
}
